import moment from 'moment';
import styled from '@emotion/styled';
import { copyToClipboard } from '@tecton';
import { Flyout } from '@tecton/ContexualComponents/Flyout';
import {
  Table,
  Badge,
  KeyValueList,
  Input,
  Link,
  Checkbox,
  Button,
  Popover,
  ComboFilter,
  FlexGroup,
  EmptyProps,
  Loading,
  Icon,
} from '@lithos';
import { ExtendedTableColumn } from '@lithos/Table';
import { ComboFilterListSelector, ComboFilterDateSelector, ComboFilterCategory } from '@lithos/ComboFilter';
import { useEffect, useRef, useState, type FC, useMemo, ReactNode } from 'react';
import TableViewLayout from '@lithos/TableViewLayout';
import { BadgeColor } from '@lithos/Badge';
import { useSearchParams } from 'react-router-dom';
import { useListEnvironments } from '../../../api/compute';
import {
  RemoteComputeType,
  RemoteEnvironmentStatus,
} from '../../../types/tecton_proto/data/remote_compute_environment';
import { ContainerImage } from '../../../types/tecton_proto/common/container_image';

const STORAGE_KEY = 'compute-environments-columns';
export interface ColumnVisibilityState {
  name: string;
  visible: boolean;
}

// TODO: This is a temporary divider component. Add to @lithos.
const Divider = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.v1.colors.border.default};
  padding-bottom: ${({ theme }) => theme.v1.size['2']}px;
`;

// Mapping Environment status for Badge label and color.
const remoteEnvironmentStatusMap: Record<
  RemoteEnvironmentStatus,
  {
    label: string;
    color: BadgeColor;
  }
> = {
  [RemoteEnvironmentStatus.REMOTE_ENVIRONMENT_STATUS_PENDING]: {
    label: 'Pending',
    color: 'blueMist',
  },
  [RemoteEnvironmentStatus.REMOTE_ENVIRONMENT_STATUS_READY]: {
    label: 'Ready',
    color: 'green',
  },
  [RemoteEnvironmentStatus.REMOTE_ENVIRONMENT_STATUS_ERROR]: {
    label: 'Error',
    color: 'red',
  },
  [RemoteEnvironmentStatus.REMOTE_ENVIRONMENT_STATUS_DELETING]: {
    label: 'Deleting',
    color: 'yellow',
  },
  [RemoteEnvironmentStatus.REMOTE_ENVIRONMENT_STATUS_DELETION_FAILED]: {
    label: 'Deletion Failed',
    color: 'red',
  },
};

const remoteComputeType: Record<RemoteComputeType, string> = {
  [RemoteComputeType.REMOTE_COMPUTE_TYPE_CORE]: 'Core',
  [RemoteComputeType.REMOTE_COMPUTE_TYPE_EXTENDED]: 'Extended',
  [RemoteComputeType.REMOTE_COMPUTE_TYPE_CUSTOM]: 'Custom',
};

type Environment = {
  id: string;
  name?: string;
  description?: string;
  type?: RemoteComputeType;
  createdAt?: string;
  updatedAt?: string;
  resolvedRequirements?: string;
  status?: RemoteEnvironmentStatus;
  materializationVersion?: string;
  sdkVersion?: string;
  remoteFunctionURL?: string;
  image_info?: ContainerImage;
};

// pip dependency
export function renderDependencyList(dependencies: string | undefined): ReactNode {
  if (!dependencies) return 'n/a';
  return (
    dependencies
      .match(/^[a-zA-Z0-9_.-]+==[\w.-]+/gm) // Extract all package==version lines
      ?.map((dep, idx) => <div key={idx}>{dep}</div>) || 'n/a'
  );
}

const FlyoutContent: FC<{ environment: Environment; canScrollToPip: boolean }> = ({ environment, canScrollToPip }) => {
  const pipDependenciesRef = useRef<HTMLDivElement>(null);
  const {
    id,
    name,
    type,
    description,
    createdAt,
    updatedAt,
    resolvedRequirements,
    materializationVersion,
    sdkVersion,
    status,
  } = environment;

  useEffect(() => {
    if (canScrollToPip) {
      pipDependenciesRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [canScrollToPip]);

  const environmentStatus =
    remoteEnvironmentStatusMap[status ?? RemoteEnvironmentStatus.REMOTE_ENVIRONMENT_STATUS_ERROR];
  const remoteType = type ? remoteComputeType[type] : 'n/a';

  const items = [
    { title: 'Name', content: name },
    {
      title: 'ID',
      content: (
        <button
          style={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', cursor: 'pointer' }}
          onClick={() => copyToClipboard(id ?? '')}
        >
          {id} <Icon type="mono" size="s" icon="Copy" />
        </button>
      ),
    },
    {
      title: 'Status',
      content: <Badge label={environmentStatus.label} color={environmentStatus.color} size="s" />,
    },
    {
      title: 'Type',
      content: remoteType,
    },
    { title: 'Materialization Version', content: materializationVersion ?? 'n/a' },
    { title: 'Tecton Runtime Version', content: sdkVersion ?? 'n/a' },
    { title: 'Description', content: description },
    { title: 'Created At', content: moment(createdAt).utc().format('YYYY-MM-DD HH:mm UTC') },
    { title: 'Last Updated', content: moment(updatedAt).utc().format('YYYY-MM-DD HH:mm UTC') },
  ];

  const pipDependencies = [
    {
      title: 'Pip Dependencies',
      content: renderDependencyList(resolvedRequirements),
    },
  ];

  return (
    <>
      <KeyValueList items={items} />
      <Divider />
      <div ref={pipDependenciesRef} style={{ marginTop: 24 }}>
        <KeyValueList items={pipDependencies} />
      </div>
    </>
  );
};

// Might want to reuse this at some point?
// UTIL for merging local storage state on top of default state
function mergeStoredStateWithDefaults<T extends { name: string }>(defaultState: T[], stored: T[] | null): T[] {
  if (!stored) return defaultState;
  const storedMap = new Map(stored.map((item) => [item.name, item]));
  return defaultState.map((item) => ({
    ...item,
    ...(storedMap.has(item.name) ? storedMap.get(item.name) : {}),
  }));
}

const ComputeEnvironmentsContainer: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const environmentId = searchParams.get('id');

  const [searchValue, setSearchValue] = useState<string>('');
  const [canShowFlyout, setCanShowFlyout] = useState(!!environmentId);
  const [flyoutEnvironment, setFlyoutEnvironment] = useState<Environment | undefined>();
  const [canScrollToPip, setCanScrollToPip] = useState(false);

  const { data, isLoading } = useListEnvironments();

  const [sortKey, setSortKey] = useState<'status' | 'name' | 'createdAt' | 'updatedAt' | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');

  // ComboFilter state
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedSdkVersions, setSelectedSdkVersions] = useState<string[]>([]);
  const [selectedMaterializationVersions, setSelectedMaterializationVersions] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [createdAtRange, setCreatedAtRange] = useState<{ start: Date; end: Date }>({
    start: new Date(0),
    end: new Date(),
  });
  const [initialCreatedAtRange, setInitialCreatedAtRange] = useState<{ start: Date; end: Date } | null>(null);
  const [updatedAtRange, setUpdatedAtRange] = useState<{ start: Date; end: Date }>({
    start: new Date(0),
    end: new Date(),
  });
  const [initialUpdatedAtRange, setInitialUpdatedAtRange] = useState<{ start: Date; end: Date } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ComboFilterCategory | undefined>(undefined);

  const defaultColumnState = [
    { name: 'Status', visible: true },
    { name: 'Name', visible: true },
    { name: 'Type', visible: true },
    { name: 'Pip Dependencies', visible: true },
    { name: 'Materialization Version', visible: true },
    { name: 'Tecton Runtime Version', visible: true },
    { name: 'Created At (UTC)', visible: true },
    { name: 'Updated At (UTC)', visible: true },
  ];
  const [columnState, setColumnState] = useState<ColumnVisibilityState[]>(() => {
    const stored = window?.localStorage?.getItem(STORAGE_KEY) || null;
    if (!stored) return defaultColumnState;
    try {
      const parsed: ColumnVisibilityState[] = JSON.parse(stored);
      return mergeStoredStateWithDefaults(defaultColumnState, parsed);
    } catch {
      return defaultColumnState;
    }
  });

  const [activeHeading, setActiveHeading] = useState<string | undefined>(undefined);
  // Compute unique filter options from data
  const allItems: Environment[] = useMemo(
    () =>
      data?.remote_environments?.map((environment) => {
        const {
          id,
          name,
          type,
          created_at: createdAt,
          updated_at: updatedAt,
          resolved_requirements,
          status,
          realtime_job_environment,
          sdk_version,
        } = environment;
        return {
          id: id ?? (`environment_${name}` as string),
          name,
          type,
          createdAt,
          updatedAt,
          resolvedRequirements: resolved_requirements,
          status,
          materializationVersion: realtime_job_environment?.tecton_transform_runtime_version,
          sdkVersion: sdk_version,
        };
      }) || [],
    [data, columnState]
  );

  // Populate filter options
  const statusOptions = useMemo(
    () =>
      Array.from(new Set(allItems.map((item) => item.status).filter(Boolean))).map(
        (status) => remoteEnvironmentStatusMap[status as RemoteEnvironmentStatus]?.label || String(status)
      ),
    [allItems]
  );
  const sdkVersionOptions = useMemo(
    () => Array.from(new Set(allItems.map((item) => item.sdkVersion).filter(Boolean))) as string[],
    [allItems]
  );
  const materializationVersionOptions = useMemo(
    () => Array.from(new Set(allItems.map((item) => item.materializationVersion).filter(Boolean))) as string[],
    [allItems]
  );
  const typeOptions = useMemo(
    () =>
      Array.from(new Set(allItems.map((item) => item.type).filter(Boolean))).map(
        (type) => remoteComputeType[type as RemoteComputeType]
      ) as string[],
    [allItems]
  );

  // ComboFilter
  const createdAtActive =
    !!initialCreatedAtRange &&
    (createdAtRange.start.getTime() !== initialCreatedAtRange.start.getTime() ||
      createdAtRange.end.getTime() !== initialCreatedAtRange.end.getTime());
  const updatedAtActive =
    !!initialUpdatedAtRange &&
    (updatedAtRange.start.getTime() !== initialUpdatedAtRange.start.getTime() ||
      updatedAtRange.end.getTime() !== initialUpdatedAtRange.end.getTime());
  const hasAnyActiveFilters =
    selectedStatus.length > 0 ||
    selectedSdkVersions.length > 0 ||
    selectedMaterializationVersions.length > 0 ||
    createdAtActive ||
    selectedTypes.length > 0 ||
    updatedAtActive;
  const activeFilterCount =
    selectedStatus.length +
    selectedSdkVersions.length +
    selectedTypes.length +
    selectedMaterializationVersions.length +
    (createdAtActive ? 1 : 0) +
    (updatedAtActive ? 1 : 0);
  const resetFilters = () => {
    setSelectedStatus([]);
    setSelectedTypes([]);
    setSelectedSdkVersions([]);
    setSelectedMaterializationVersions([]);
    if (initialCreatedAtRange) setCreatedAtRange(initialCreatedAtRange);
    if (initialUpdatedAtRange) setUpdatedAtRange(initialUpdatedAtRange);
    setSelectedCategory(undefined);
  };
  const isDisabled = isLoading;

  // ComboFilter categories
  const categories: ComboFilterCategory[] = [
    {
      name: 'Status',
      icon: 'Running' as any,
      activeCount: selectedStatus.length,
      resetCallback: () => setSelectedStatus([]),
      popover: (
        <ComboFilterListSelector
          selected={selectedStatus}
          setSelected={setSelectedStatus}
          items={statusOptions}
          title={'Status'}
        />
      ),
    },
    {
      name: 'Type',
      icon: 'Puzzle' as any,
      activeCount: selectedTypes.length,
      resetCallback: () => setSelectedTypes([]),
      popover: (
        <ComboFilterListSelector
          selected={selectedTypes}
          setSelected={setSelectedTypes}
          items={typeOptions}
          title={'Type'}
        />
      ),
    },
    {
      name: 'Tecton Runtime Version',
      icon: 'ChipSparkle' as any,
      activeCount: selectedSdkVersions.length,
      resetCallback: () => setSelectedSdkVersions([]),
      popover: (
        <ComboFilterListSelector
          selected={selectedSdkVersions}
          setSelected={setSelectedSdkVersions}
          items={sdkVersionOptions}
          title={'Tecton Runtime Version'}
        />
      ),
    },
    {
      name: 'Materialization Version',
      icon: 'ComputeV2' as any,
      activeCount: selectedMaterializationVersions.length,
      resetCallback: () => setSelectedMaterializationVersions([]),
      popover: (
        <ComboFilterListSelector
          selected={selectedMaterializationVersions}
          setSelected={setSelectedMaterializationVersions}
          items={materializationVersionOptions}
          title={'Materialization Version'}
        />
      ),
    },
    {
      name: 'Created At',
      icon: 'Timestamp' as any,
      activeCount: createdAtActive ? 1 : 0,
      resetCallback: () => {
        if (initialCreatedAtRange) setCreatedAtRange(initialCreatedAtRange);
      },
      popover: initialCreatedAtRange ? (
        <ComboFilterDateSelector
          start={createdAtRange.start}
          setStart={(date: Date | null | undefined) =>
            setCreatedAtRange((prev) => ({ ...prev, start: date || initialCreatedAtRange.start }))
          }
          end={createdAtRange.end}
          setEnd={(date: Date | null | undefined) =>
            setCreatedAtRange((prev) => ({ ...prev, end: date || initialCreatedAtRange.end }))
          }
          minDate={initialCreatedAtRange.start}
          maxDate={initialCreatedAtRange.end}
        />
      ) : null,
    },
    {
      name: 'Updated At',
      icon: 'Timestamp' as any,
      activeCount: updatedAtActive ? 1 : 0,
      resetCallback: () => {
        if (initialUpdatedAtRange) setUpdatedAtRange(initialUpdatedAtRange);
      },
      popover: initialUpdatedAtRange ? (
        <ComboFilterDateSelector
          start={updatedAtRange.start}
          setStart={(date: Date | null | undefined) =>
            setUpdatedAtRange((prev) => ({ ...prev, start: date || initialUpdatedAtRange.start }))
          }
          end={updatedAtRange.end}
          setEnd={(date: Date | null | undefined) =>
            setUpdatedAtRange((prev) => ({ ...prev, end: date || initialUpdatedAtRange.end }))
          }
          minDate={initialUpdatedAtRange.start}
          maxDate={initialUpdatedAtRange.end}
        />
      ) : null,
    },
  ];

  // Filtering logic
  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      // Search
      if (searchValue) {
        const normalizedValue = searchValue.toLowerCase();
        if (
          !(
            item?.name?.toLowerCase().includes(normalizedValue) ||
            item?.id?.toLowerCase().includes(normalizedValue) ||
            (item?.type && remoteComputeType[item.type]?.toLowerCase().includes(normalizedValue)) ||
            item?.description?.toLowerCase().includes(normalizedValue) ||
            item?.createdAt?.toLowerCase().includes(normalizedValue) ||
            item?.type?.toLowerCase().includes(normalizedValue) ||
            item?.materializationVersion?.toLowerCase().includes(normalizedValue) ||
            item?.sdkVersion?.toLowerCase().includes(normalizedValue)
          )
        ) {
          return false;
        }
      }
      // Status filter
      if (selectedStatus.length > 0) {
        const label =
          remoteEnvironmentStatusMap[item.status ?? RemoteEnvironmentStatus.REMOTE_ENVIRONMENT_STATUS_ERROR]?.label;
        if (!selectedStatus.includes(label)) return false;
      }
      // Type filter
      if (selectedTypes.length > 0) {
        const label = remoteComputeType[item.type as RemoteComputeType];
        if (!selectedTypes.includes(label)) return false;
      }
      // Sdk version filter
      if (selectedSdkVersions.length > 0) {
        if (!selectedSdkVersions.includes(item.sdkVersion ?? '')) return false;
      }
      // Materialization version filter
      if (selectedMaterializationVersions.length > 0) {
        if (!selectedMaterializationVersions.includes(item.materializationVersion ?? '')) return false;
      }
      // Created at filter
      if (item.createdAt) {
        const created = new Date(item.createdAt);
        if (created < createdAtRange.start || created > createdAtRange.end) return false;
      }
      // Updated at filter
      if (item.updatedAt) {
        const updated = new Date(item.updatedAt);
        if (updated < updatedAtRange.start || updated > updatedAtRange.end) return false;
      }
      return true;
    });
  }, [
    allItems,
    searchValue,
    selectedStatus,
    selectedTypes,
    selectedSdkVersions,
    selectedMaterializationVersions,
    createdAtRange,
    updatedAtRange,
  ]);

  useEffect(() => {
    if (environmentId) {
      const foundEnvironment = filteredItems.find((env) => env.id === environmentId);
      setFlyoutEnvironment(foundEnvironment);
      setCanShowFlyout(!!foundEnvironment);
    } else {
      setFlyoutEnvironment(undefined);
      setCanShowFlyout(false);
    }
  }, [environmentId, filteredItems]);

  const columnsSelector = useMemo(
    () => (
      <Popover
        trigger={<Button type="secondary" label="Columns" iconRight="ChevronDown" />}
        body={
          <div>
            {columnState.map((column) => (
              <div key={column.name} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <Checkbox
                  label={column.name}
                  size="s"
                  state={column.visible ? 'on' : 'off'}
                  onClick={() => {
                    setColumnState((prev: ColumnVisibilityState[]) => {
                      const next = prev.map((selectableColumn: ColumnVisibilityState) =>
                        selectableColumn.name === column.name
                          ? { ...selectableColumn, visible: !selectableColumn.visible }
                          : selectableColumn
                      );
                      if (typeof window !== 'undefined') {
                        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
                      }
                      return next;
                    });
                  }}
                />
              </div>
            ))}
          </div>
        }
      />
    ),
    [columnState, setColumnState]
  );

  const filterEmpty: EmptyProps = {
    actions: <></>,
    placement: 'full-page',
    useCase: 'empty',
    title: 'No Results',
    description: 'No Environments match the current filters. Try adjusting your filters or search query.',
  };

  // Filtering logic
  const filteredColumns: ExtendedTableColumn<Environment>[] = [
    {
      name: 'Status',
      id: 'status',
      key: 'status',
      field: 'status',
      columnWidth: 's',
      sortCallback: (direction: 'ascending' | 'descending') => {
        setSortKey('status');
        setSortDirection(direction);
      },
      isSortActive: sortKey === 'status',
      sortDirection: sortKey === 'status' ? sortDirection : undefined,
      content: ({ status }: Environment) => {
        const environmentStatus =
          remoteEnvironmentStatusMap[status ?? RemoteEnvironmentStatus.REMOTE_ENVIRONMENT_STATUS_ERROR];
        return <Badge label={environmentStatus.label} color={environmentStatus.color} size="s" />;
      },
    },
    {
      name: 'Name',
      id: 'name',
      key: 'name',
      field: 'name',
      sortCallback: (direction: 'ascending' | 'descending') => {
        setSortKey('name');
        setSortDirection(direction);
      },
      isSortActive: sortKey === 'name',
      sortDirection: sortKey === 'name' ? sortDirection : undefined,
      content: (environment: Environment) => {
        return (
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              setCanShowFlyout(!canShowFlyout);
              setFlyoutEnvironment(environment);
              setCanScrollToPip(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setCanShowFlyout(!canShowFlyout);
                setFlyoutEnvironment(environment);
                setCanScrollToPip(false);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <Link url={`?id=${environment.id}`} name={environment.name} />
          </div>
        );
      },
    },
    {
      name: 'Type',
      id: 'type',
      key: 'type',
      field: 'type',
      content: ({ type }: Environment) => remoteComputeType[type as RemoteComputeType] ?? 'n/a',
    },
    {
      name: 'Pip Dependencies',
      id: 'resolvedRequirements',
      key: 'resolvedRequirements',
      field: 'resolvedRequirements',
      content: (environment: Environment) => {
        const count = environment.resolvedRequirements?.split('\n').length;
        return count === undefined ? (
          'n/a'
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              setCanShowFlyout(!canShowFlyout);
              setFlyoutEnvironment(environment);
              setCanScrollToPip(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setCanShowFlyout(!canShowFlyout);
                setFlyoutEnvironment(environment);
                setCanScrollToPip(true);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <Link
              url={`?id=${environment.id}`}
              name={environment.resolvedRequirements?.split('\n').length}
              icon={'Log'}
            />
          </div>
        );
      },
    },
    {
      name: 'Materialization Version',
      id: 'materializationVersion',
      key: 'materializationVersion',
      field: 'materializationVersion',
      content: ({ materializationVersion }: Environment) => materializationVersion ?? 'n/a',
    },
    {
      name: 'Tecton Runtime Version',
      id: 'sdkVersion',
      key: 'sdkVersion',
      field: 'sdkVersion',
      content: ({ sdkVersion }: Environment) => sdkVersion ?? 'n/a',
    },
    {
      name: 'Description',
      id: 'description',
      key: 'description',
      field: 'description',
      content: ({ description }: Environment) => description,
    },
    {
      name: 'Created At (UTC)',
      id: 'createdAt',
      key: 'createdAt',
      field: 'createdAt',
      width: '200px',
      sortCallback: (direction: 'ascending' | 'descending') => {
        setSortKey('createdAt');
        setSortDirection(direction);
      },
      isSortActive: sortKey === 'createdAt',
      sortDirection: sortKey === 'createdAt' ? sortDirection : undefined,
      content: ({ createdAt }: Environment) => {
        const date = moment(createdAt).utc().format('YYYY-MM-DD HH:mm UTC');
        return date;
      },
    },
    {
      name: 'Updated At (UTC)',
      id: 'updatedAt',
      key: 'updatedAt',
      field: 'updatedAt',
      sortCallback: (direction: 'ascending' | 'descending') => {
        setSortKey('updatedAt');
        setSortDirection(direction);
      },
      isSortActive: sortKey === 'updatedAt',
      sortDirection: sortKey === 'updatedAt' ? sortDirection : undefined,
      content: ({ updatedAt }: Environment) => {
        const date = moment(updatedAt).utc().format('YYYY-MM-DD HH:mm UTC');
        return date;
      },
    },
  ]
    .map((column) => {
      // Use id for matching visibility
      const matching = columnState.find(
        (stateColumn: ColumnVisibilityState) => stateColumn.name === column.name || stateColumn.name === column.id
      );
      if (!matching || matching.visible !== true) {
        return undefined;
      }
      return {
        ...column,
        // name: column.name, // already set above
        isActive: column.id === activeHeading,
        setIsActive: (arg: boolean) => {
          if (arg) {
            setActiveHeading(column.id);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn: ColumnVisibilityState) => {
            if (selectableColumn.name === column.name || selectableColumn.name === column.id) {
              return {
                name: selectableColumn.name,
                visible: !selectableColumn.visible,
              };
            }
            return selectableColumn;
          });
          setColumnState(newColumnState);
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(newColumnState));
          }
        },
        columnsContent: columnsSelector,
      } as ExtendedTableColumn<Environment>;
    })
    .filter((column): column is ExtendedTableColumn<Environment> => column !== undefined);

  // Created At and Updated At range
  useEffect(() => {
    if (allItems.length > 0) {
      const dates = allItems
        .map((item) => item.createdAt)
        .filter(Boolean)
        .map((d) => new Date(d!));
      if (dates.length > 0) {
        const min = new Date(Math.min(...dates.map((d) => d.getTime())));
        const max = new Date(Math.max(...dates.map((d) => d.getTime())));
        // add edges to range to avoid error on min/max date
        const paddedMin = new Date(min.getTime() - 24 * 60 * 60 * 1000);
        const paddedMax = new Date(max.getTime() + 24 * 60 * 60 * 1000);
        if (!initialCreatedAtRange) {
          setInitialCreatedAtRange({ start: paddedMin, end: paddedMax });
          setCreatedAtRange({ start: paddedMin, end: paddedMax });
        }
      }
      // Updated At
      const updatedDates = allItems
        .map((item) => item.updatedAt)
        .filter(Boolean)
        .map((d) => new Date(d!));
      if (updatedDates.length > 0) {
        const min = new Date(Math.min(...updatedDates.map((d) => d.getTime())));
        const max = new Date(Math.max(...updatedDates.map((d) => d.getTime())));
        const paddedMin = new Date(min.getTime() - 24 * 60 * 60 * 1000);
        const paddedMax = new Date(max.getTime() + 24 * 60 * 60 * 1000);
        if (!initialUpdatedAtRange) {
          setInitialUpdatedAtRange({ start: paddedMin, end: paddedMax });
          setUpdatedAtRange({ start: paddedMin, end: paddedMax });
        }
      }
    }
  }, [allItems, allItems.length, initialCreatedAtRange, initialUpdatedAtRange]);
  if (isLoading) {
    return (
      <FlexGroup justifyContent="center" gapSize="4">
        <Loading size="xl" />
      </FlexGroup>
    );
  }

  // Custom sort function (restore this, as it was removed by accident)
  const sortItems = (items: Environment[]) => {
    if (!sortKey) return items;
    return [...items].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      if (sortKey === 'createdAt' || sortKey === 'updatedAt') {
        // Compare as dates
        const aDate = aValue ? new Date(aValue as string).getTime() : 0;
        const bDate = bValue ? new Date(bValue as string).getTime() : 0;
        return sortDirection === 'ascending' ? aDate - bDate : bDate - aDate;
      } else {
        // Compare as strings
        const aStr = (aValue ?? '').toString().toLowerCase();
        const bStr = (bValue ?? '').toString().toLowerCase();
        if (aStr < bStr) return sortDirection === 'ascending' ? -1 : 1;
        if (aStr > bStr) return sortDirection === 'ascending' ? 1 : -1;
        return 0;
      }
    });
  };

  return (
    <>
      <TableViewLayout
        searchInput={
          <FlexGroup alignItems="center">
            <Input
              type="string"
              placeholder="Search"
              value={searchValue}
              onChange={(value) => setSearchValue(value)}
              icon="Search"
              size="s"
            />
            <ComboFilter
              icon={'Filters'}
              hasAnyActiveFilters={hasAnyActiveFilters}
              activeFilterCount={activeFilterCount}
              resetFilters={resetFilters}
              categories={categories}
              setSelectedCategory={setSelectedCategory}
              selectedCategory={selectedCategory}
              disabled={isDisabled}
            />
          </FlexGroup>
        }
        filterAndColumnControls={<FlexGroup alignItems="center">{columnsSelector}</FlexGroup>}
        table={<Table items={sortItems(filteredItems)} columns={filteredColumns} emptyProps={filterEmpty} />}
      />
      <Flyout
        title={flyoutEnvironment?.name}
        content={
          flyoutEnvironment ? <FlyoutContent environment={flyoutEnvironment} canScrollToPip={canScrollToPip} /> : null
        }
        isOpen={canShowFlyout}
        setIsOpen={(open) => {
          if (!open) {
            setCanShowFlyout(false);
            setSearchParams(undefined);
          }
        }}
      />
    </>
  );
};

export default ComputeEnvironmentsContainer;
